<!-- eslint-disable vue/no-mutating-props -- filters is a shared reactive object, mutation is intentional -->
<template>
  <div
    class="px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-3"
  >
    <!-- Team multi-select dropdown -->
    <div class="relative">
      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
        >Blik (team)</label
      >
      <Listbox v-slot="{ open: teamOpen }" v-model="filters.team_ids" multiple>
        <div class="relative">
          <ListboxButton
            class="relative w-full cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            :class="
              teamOpen
                ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700 text-gray-900 dark:text-white'
                : 'rounded-lg text-gray-500 dark:text-gray-400'
            "
          >
            <span class="block truncate">
              {{
                filters.team_ids.length === 0
                  ? 'Alle teams'
                  : `${filters.team_ids.length} geselecteerd`
              }}
            </span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <ListboxOptions
            class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
          >
            <ListboxOption
              v-for="team in teams"
              :key="team.simplicate_id"
              :value="team.simplicate_id"
              v-slot="{ active, selected }"
              as="template"
            >
              <li
                :class="[
                  active
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                    : 'text-gray-900 dark:text-gray-200',
                  'flex items-center gap-2 cursor-pointer select-none py-2 px-3'
                ]"
              >
                <CheckIcon
                  v-if="selected"
                  class="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400"
                  aria-hidden="true"
                />
                <span class="block whitespace-nowrap">
                  {{ team.name }} ({{ getTeamEmployeeCount(team) }})
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </div>
      </Listbox>
    </div>

    <!-- Function multi-select dropdown -->
    <div class="relative">
      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Functie</label>
      <Listbox v-slot="{ open: functionOpen }" v-model="filters.functions" multiple>
        <div class="relative">
          <ListboxButton
            class="relative w-full cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-3 pr-8 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            :class="
              functionOpen
                ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700 text-gray-900 dark:text-white'
                : 'rounded-lg text-gray-500 dark:text-gray-400'
            "
          >
            <span class="block truncate">
              {{
                filters.functions.length === 0
                  ? 'Alle functies'
                  : `${filters.functions.length} geselecteerd`
              }}
            </span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <ListboxOptions
            class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
          >
            <ListboxOption
              v-for="functionName in uniqueFunctions"
              :key="functionName"
              :value="functionName"
              v-slot="{ active, selected }"
              as="template"
            >
              <li
                :class="[
                  active
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                    : 'text-gray-900 dark:text-gray-200',
                  'flex items-center gap-2 cursor-pointer select-none py-2 px-3'
                ]"
              >
                <CheckIcon
                  v-if="selected"
                  class="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400"
                  aria-hidden="true"
                />
                <span class="block whitespace-nowrap">
                  {{ functionName }} ({{ getFunctionEmployeeCount(functionName) }})
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </div>
      </Listbox>
    </div>

    <div>
      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Zoeken</label>
      <input
        ref="searchInput"
        v-model="filters.search"
        type="text"
        placeholder="Zoek op naam..."
        class="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
    <div>
      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
        >DPW (dagen per week)</label
      >
      <DpwInput
        v-model="dpw"
        placeholder="Bijv. 2.5"
        input-class="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
    <AvailabilityModeSelect v-model="availabilityMode" compact />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/24/outline'
import DpwInput from '@/components/common/inputs/DpwInput.vue'
import AvailabilityModeSelect from '@/components/common/inputs/AvailabilityModeSelect.vue'

const props = defineProps({
  /** Reactive filters object (mutated directly by child via v-model) */
  filters: {
    type: Object,
    required: true
  },
  teams: {
    type: Array,
    default: () => []
  },
  uniqueFunctions: {
    type: Array,
    default: () => []
  },
  employees: {
    type: Array,
    default: () => []
  }
})

/** Two-way binding for DPW value */
const dpw = defineModel('dpw', { type: Number })

/** Two-way binding for availability mode */
const availabilityMode = defineModel('availabilityMode', { type: String, default: 'presourcing' })

const searchInput = ref(null)

const employeeBelongsToTeam = (employee, teamId) =>
  (employee.teams || []).some(team => (team.simplicate_id || team.team_id) === teamId)

const getTeamEmployeeCount = team =>
  props.employees.filter(
    employee =>
      employeeBelongsToTeam(employee, team.simplicate_id) &&
      (props.filters.functions.length === 0 || props.filters.functions.includes(employee.function))
  ).length

const getFunctionEmployeeCount = functionName =>
  props.employees.filter(
    employee =>
      employee.function === functionName &&
      (props.filters.team_ids.length === 0 ||
        props.filters.team_ids.some(teamId => employeeBelongsToTeam(employee, teamId)))
  ).length

function focusSearch() {
  searchInput.value?.focus?.()
}

defineExpose({ focusSearch })
</script>
